// Try-it demo (c) — forecast entry. Two editable vendor/purpose rows with
// twelve monthly inputs; quarter and annual cells derive live as you type.
// "Save" appends a snapshot to a visible history timeline — framed in the
// client's own vocabulary: your Spring revision is just a snapshot, the
// original stays. Every save is kept; nothing is ever overwritten.

import React, { useState } from 'react';
import { PERIOD_LABELS, fmtUSD } from './mockData';

const INITIAL_LINES = [
  { id: 'fenwick', vendor: 'Fenwick', purpose: 'IRS Assistance', vals: Array(12).fill('2000') },
  { id: 'deloitte', vendor: 'Deloitte', purpose: 'IRS Assistance', vals: Array(12).fill('4000') },
];

function parse(v) {
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function quarters(vals) {
  const out = [];
  for (let q = 0; q < 4; q += 1) {
    out.push(vals.slice(q * 3, q * 3 + 3).reduce((s, v) => s + parse(v), 0));
  }
  return out;
}

export default function DemoForecast() {
  const [lines, setLines] = useState(INITIAL_LINES);
  const [reason, setReason] = useState('Spring revision');
  const [history, setHistory] = useState([
    { id: 1, reason: 'Original budget', when: 'saved Feb 3, 2026', annual: 72000, original: true },
  ]);

  const setVal = (lineIdx, periodIdx, value) => {
    setLines((prev) =>
      prev.map((l, i) =>
        i === lineIdx ? { ...l, vals: l.vals.map((v, j) => (j === periodIdx ? value : v)) } : l
      )
    );
  };

  const lineQuarters = lines.map((l) => quarters(l.vals));
  const lineAnnuals = lineQuarters.map((qs) => qs.reduce((s, v) => s + v, 0));
  const totalQuarters = [0, 1, 2, 3].map((q) =>
    lineQuarters.reduce((s, qs) => s + qs[q], 0)
  );
  const grandAnnual = lineAnnuals.reduce((s, v) => s + v, 0);

  const save = () => {
    setHistory((prev) => [
      {
        id: prev.length + 1,
        reason: reason.trim() || 'Spring revision',
        when: 'saved just now',
        annual: grandAnnual,
        fresh: true,
      },
      ...prev.map((h) => ({ ...h, fresh: false })),
    ]);
  };

  return (
    <div className="tut-fc">
      <div className="tut-fc-banner">
        Your Spring revision is just a snapshot — <strong>the original stays.</strong>
      </div>

      <div className="tut-fc-scroll">
        <table className="tut-fc-table">
          <thead>
            <tr>
              <th className="nm">Vendor / purpose</th>
              {PERIOD_LABELS.map((p, i) => (
                <th key={p}>
                  P{i + 1}
                  <span className="mo">{p}</span>
                </th>
              ))}
              {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
                <th key={q} className="dv">
                  {q}
                </th>
              ))}
              <th className="dv an">Annual</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, li) => (
              <tr key={line.id}>
                <td className="nm">
                  <strong>{line.vendor}</strong> — {line.purpose}
                </td>
                {line.vals.map((v, pi) => (
                  <td key={pi}>
                    <input
                      className="tut-fc-input num"
                      inputMode="numeric"
                      value={v}
                      onChange={(e) => setVal(li, pi, e.target.value)}
                      aria-label={`${line.vendor} P${pi + 1}`}
                    />
                  </td>
                ))}
                {lineQuarters[li].map((q, qi) => (
                  <td key={qi} className="dv num">
                    {q.toLocaleString('en-US')}
                  </td>
                ))}
                <td className="dv an num">{lineAnnuals[li].toLocaleString('en-US')}</td>
              </tr>
            ))}
            <tr className="total">
              <td className="nm">IRS Assistance — total</td>
              <td colSpan={12} className="fill">
                type in any month — the totals follow
              </td>
              {totalQuarters.map((q, qi) => (
                <td key={qi} className="dv num">
                  {q.toLocaleString('en-US')}
                </td>
              ))}
              <td className="dv an num">{grandAnnual.toLocaleString('en-US')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="tut-fc-save">
        <label htmlFor="tut-fc-reason">Snapshot reason</label>
        <input
          id="tut-fc-reason"
          className="tut-fc-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button type="button" className="tut-btn primary" onClick={save}>
          Save snapshot
        </button>
        <span className="hint">every save is kept; nothing is ever overwritten</span>
      </div>

      <ol className="tut-fc-hist">
        {history.map((h) => (
          <li key={h.id} className={`tut-fc-snap${h.fresh ? ' new' : ''}${h.original ? ' orig' : ''}`}>
            <span className="dot" />
            <span className="rsn">{h.reason}</span>
            <span className="when">{h.when}</span>
            <span className="ann num">annual {fmtUSD(h.annual)}</span>
            {h.original && <span className="tut-badge soft">original — untouched</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}
